import { Injectable } from '@angular/core';
import { Routes } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { appRoutes } from '../../download-portal.routes';
import {
  DownloadDetail,
  TargetTab,
  UriId,
} from '../../model/download-portal-uris';
import { ProductService } from './product.service';
import { SmeService } from './sme.service';

@Injectable({
  providedIn: 'root',
})
export class DownloadPortalUrisService {
  routes: Routes = appRoutes;

  constructor(
    private readonly smeService: SmeService,
    private readonly productService: ProductService,
  ) {}

  /**
   * Use this method to get the uri string of one of the list view pages for Basis-Software, Zusatzsoftware or Mittelstand.
   * @param targetTab TargetTab
   */
  getTabUri(targetTab: TargetTab): string {
    return this.findRoute(targetTab);
  }

  /**
   * Use this method to get the uri string of one of the detail view page for a specific Zusatzsoftware
   * @param additionalSoftwareId string
   */
  getAdditionalSoftwareDetailsPage(additionalSoftwareId: string): string {
    const rawDetailRoute = this.findRoute(
      DownloadDetail.ADDITIONAL_SOFTWARE_DETAIL,
    );

    return this.getHydratedRoute(rawDetailRoute, {
      ':id': additionalSoftwareId,
    });
  }

  /**
   * Use this method to get the uri string of main release details page for the given HKNR
   * @param hknr number
   */
  getMainReleaseDetailPage(hknr: number): Observable<string> {
    return this.smeService
      .getMainReleases({ mainComponents: `${hknr}`, isArchived: false })
      .pipe(
        map(mainReleases => {
          if (mainReleases.length === 0) {
            return '';
          }

          const rawDetailRoute = this.findRoute(
            DownloadDetail.MAIN_RELEASE_DETAIL,
          );

          const latestMainRelease = mainReleases.sort((a, b) => {
            return (
              new Date(a.published_date || 0).valueOf() -
              new Date(b.published_date || 0).valueOf()
            );
          })[0];

          return this.getHydratedRoute(rawDetailRoute, {
            ':id': latestMainRelease.id,
          });
        }),
      );
  }

  /**
   * Use this method to get the uri string of the latest service release details page
   */
  getServiceReleaseDetailPage(): Observable<string> {
    return this.smeService.getServiceReleases().pipe(
      map(serviceReleases => {
        if (serviceReleases.length === 0) {
          return '';
        }

        const rawDetailRoute = this.findRoute(
          DownloadDetail.SERVICE_RELEASE_DETAIL,
        );

        const latestServiceRelease = serviceReleases.sort((a, b) => {
          return (
            new Date(a.published_date || 0).valueOf() -
            new Date(b.published_date || 0).valueOf()
          );
        })[0];

        return this.getHydratedRoute(rawDetailRoute, {
          ':id': latestServiceRelease.id,
        });
      }),
    );
  }

  findRoute(
    uriId: UriId,
    routes: Routes = this.routes,
    originalUri: string = '',
  ): string {
    for (const route of routes) {
      const currentUri =
        route.path === '' ? originalUri : `${originalUri}/${route.path}`;
      if (route.data?.uriId === uriId) {
        return currentUri;
      }
      if (route.children) {
        const uri = this.findRoute(uriId, route.children, currentUri);
        if (uri !== '') {
          return uri;
        }
      }
    }

    return '';
  }

  private getHydratedRoute(
    rawRoute: string,
    replacementRules: Record<string, string>,
  ): string {
    let hydratedRoute = rawRoute;
    for (const stringToReplace in replacementRules) {
      if (
        Object.prototype.hasOwnProperty.call(replacementRules, stringToReplace)
      ) {
        const replacementValue = replacementRules[stringToReplace];

        if (hydratedRoute === '' || !hydratedRoute.includes(stringToReplace)) {
          return '';
        }

        hydratedRoute = hydratedRoute.replace(
          stringToReplace,
          replacementValue,
        );
      }
    }

    return hydratedRoute;
  }
}
